import { useEffect, useRef } from 'react'
import { invitationApi } from '@/api/invitationApi'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationsStore, type BillingNotif, type InvitationResponseNotif } from '@/stores/notificationsStore'

const BASE_WS_URL = (import.meta.env.VITE_API_BASE_URL ?? '')
  .replace(/^https?/, (scheme: string) => (scheme === 'https' ? 'wss' : 'ws'))
  .replace(/\/+$/, '')

const RECONNECT_DELAY_MS = 5000

export function useNotificationsWS() {
  const user = useAuthStore((s) => s.user)
  const status = useAuthStore((s) => s.status)
  const {
    setInvitations,
    addInvitation,
    addResponse,
    addBilling,
    loadAnnouncements,
    setAnnouncementTest,
    setWsStatus,
  } = useNotificationsStore()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (status !== 'authed' || !user) {
      setWsStatus('idle')
      return
    }

    let ws: WebSocket | null = null

    function connect() {
      if (!mountedRef.current) return
      setWsStatus('connecting')

      ws = new WebSocket(`${BASE_WS_URL}/ws/notifications/`)
      wsRef.current = ws

      ws.onopen = () => {
        if (!mountedRef.current) { ws?.close(); return }
        setWsStatus('connected')
        invitationApi.myPending().then((res) => {
          if (mountedRef.current) setInvitations(res.results)
        }).catch(() => {})
        void loadAnnouncements().catch(() => {})
      }

      ws.onmessage = (event: MessageEvent) => {
        if (!mountedRef.current) return
        let msg: Record<string, unknown>
        try {
          msg = JSON.parse(event.data as string) as Record<string, unknown>
        } catch {
          return
        }

        if (msg.type === 'invitations.sync') {
          const list = msg.invitations as Array<{
            invitation_id: number
            workspace_id: number
            workspace_name: string
            workspace_slug?: string
            invited_by_name: string
            role: number
            created_at: string
          }>
          const mapped = list.map((item) => ({
            id: item.invitation_id,
            workspace: item.workspace_id,
            workspace_name: item.workspace_name,
            workspace_slug: item.workspace_slug ?? '',
            invited_by: { id: 0, name: item.invited_by_name, email: '' },
            email: user!.email,
            role: item.role as 1 | 2 | 3,
            is_active: true,
            is_pending: true,
            responded_at: null,
            response: null,
            created_at: item.created_at,
            modified_at: item.created_at,
          }))
          setInvitations(mapped)
          return
        }

        if (msg.type === 'invitation.received') {
          const item = msg as {
            type: string
            invitation_id: number
            workspace_id: number
            workspace_name: string
            invited_by_name: string
            role: number
          }
          addInvitation({
            id: item.invitation_id,
            workspace: item.workspace_id,
            workspace_name: item.workspace_name,
            workspace_slug: '',
            invited_by: { id: 0, name: item.invited_by_name, email: '' },
            email: user!.email,
            role: item.role as 1 | 2 | 3,
            is_active: true,
            is_pending: true,
            responded_at: null,
            response: null,
            created_at: new Date().toISOString(),
            modified_at: new Date().toISOString(),
          })
          return
        }

        if (msg.type === 'billing.subscription_activated' || msg.type === 'billing.payment_failed') {
          const item = msg as unknown as {
            type: string
            workspace_id: number
            workspace_name: string
            workspace_slug?: string
            plan_name?: string
            reason?: string
          }
          const kind = msg.type === 'billing.subscription_activated' ? 'activated' : 'payment_failed'
          const receivedAt = new Date().toISOString()
          const notif: BillingNotif = {
            id: `bill-${kind}-${item.workspace_id}-${receivedAt}`,
            kind,
            workspace_id: item.workspace_id,
            workspace_name: item.workspace_name ?? '',
            workspace_slug: item.workspace_slug ?? '',
            plan_name: item.plan_name ?? '',
            reason: item.reason ?? '',
            received_at: receivedAt,
          }
          addBilling(notif)
          return
        }

        if (msg.type === 'announcement.published') {
          void loadAnnouncements().catch(() => {})
          return
        }

        if (msg.type === 'announcement.test') {
          setAnnouncementTest(msg.announcement as unknown as import('@/api/types').AnnouncementContent)
          return
        }

        if (msg.type === 'invitation.responded') {
          const item = msg as {
            type: string
            invitation_id: number
            workspace_id: number
            workspace_name: string
            invitee_email: string
            invitee_name: string
            response: 'accepted' | 'declined'
          }
          const notif: InvitationResponseNotif = {
            id: `inv-${item.invitation_id}`,
            invitation_id: item.invitation_id,
            workspace_id: item.workspace_id,
            workspace_name: item.workspace_name,
            invitee_email: item.invitee_email,
            invitee_name: item.invitee_name,
            response: item.response,
            received_at: new Date().toISOString(),
          }
          addResponse(notif)
        }
      }

      ws.onclose = () => {
        wsRef.current = null
        if (!mountedRef.current) return
        setWsStatus('disconnected')
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connect()
        }, RECONNECT_DELAY_MS)
      }

      ws.onerror = () => {
        ws?.close()
      }
    }

    connect()

    return () => {
      mountedRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      wsRef.current?.close()
      wsRef.current = null
      setWsStatus('idle')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user?.id])
}
